import HealthData from '../services/health-data.js';
import { getField, updateField } from 'vuex-map-fields';


// The default values so ensure data bindings work - you have to set the objects explicitly here for
// two way data binding to work when injecting as computed() into component
function defaultData() {
  return {
    general: {
      gender: null,
      birthdate: null,
      ethnicity: null,
      measurements: {
        height: {
          feet: null,
          inches: null
        },
        weight: null,
        waist: null,
        hips: null,
        pulse: null,
        bodyfat: null
      },
      why_fff: {
        reason: null,
        reason_desc: null
      },
      weight: {
        goal: null,
        target: null,
        perweek: null
      }
    },
    eating: {
      food_spiral: null,
      renal_restricted_diet: null,
      daily_fluids_goal:null,
      daily_calories_goal:null,
      daily: {
        fat: {
          serving_type: null,
          servings: null
        },
        dairy: {
          serving_type: null,
          servings: null
        },
        fruit: {
          serving_type: null,
          servings: null
        },
        breads: {
          serving_type: null,
          servings: null
        },
        protein: {
          serving_type: null,
          servings: null
        },
        vegetables: {
          serving_type: null,
          servings: null
        },
      },
      drinks: {
        water: {
          glasses_daily: null
        },
        caffeinated_sugar: {
          glasses_daily: null
        }
      },
      fast_food: {
        per_week: null
      }
    },
    medical: {
      conditions: {
        diabetes: {
          has: null,
          takes_insulin: null,
          on_insulin_pump: null,
          takes_meds: null,
          meds: null
        },
        prediabetes: {
          has: null,
          takes_meds: null,
          meds: null
        },
        hypertension: {
          has: null,
          takes_meds: null,
          meds: null
        },
        kidney_disease: {
          has: null,
          takes_meds: null,
          meds: null
        },
        heart_disease: {
          has: null,
          had_stroke: null,
          had_heart_attack: null,
          takes_meds: null,
          meds: null
        },
        high_blood_fats: {
          has: null,
          takes_meds: null,
          meds: null
        },
        liver_disease: {
          has: null,
          takes_meds: null,
          meds: null
        },
        cancer: {
          has: null,
          has_had: null,
          in_remission_for: null,
          takes_meds: null,
          meds: null
        }
      },
      family_history: {
        diabetes: null,
        hypertension: null,
        kidney_disease: null,
        heart_disease: null,
        high_blood_fats: null,
        liver_disease: null,
        cancer: null
      },
      meds: {
        herbal: {
          takes: null,
          description: null
        },
        otc: {
          takes: null,
          description: null
        }
      },
      labs: {
        pap: {
          results: null,
          years_since: null
        },
        mammogram: {
          results: null,
          years_since: null
        },
        lipid_panel: {
          results: null,
          years_since: null
        },
        colonoscopy: {
          results: null,
          years_since: null
        },
        prostate: {
          results: null,
          years_since: null
        },
        general: {
          sodium: null,
          potassium: null,
          phosphorus: null,
          magnesium: null,
          sgot_ast: null,
          glucose: null,
          sgpt_alt: null,
          cholesterol: null,
          hdl: null,
          ldl: null,
          triglycerides: null,
          bun: null,
          creatinine: null,
          bun_to_creatinine: null,
          hgb: null,
          hct: null,
          hgba1c: null,
          tsh: null,
          bone_density: null,
          blood_pressure: {
            systolic: null,
            diastolic: null,
          }
        }
      }
    },
    exercise: {
      exercise_type: null,
      duration: null,
      weekly_frequency: null,
      daily_steps: null,
      daily_steps_goal:null
    },
    lifestyle: {
      sleep: {
        days_6_8_hours: null
      },
      alcohol: {
        drinks_perweek: null,
        five_single_sitting_last_6_months: null
      },
      smoking: {
        last_smoked: null
      }
    }
  }
}

// Health data store - keep a copy of the initial payload from the API for resetting, and make modifications to the
// data property/object.  When saving, write the data to the initial to update with the newly saved data.  When resetting,
// set the data to the initial store value.  When loading, merge the incoming payload with default answers to ensure all
// necessary fields are present - allowing two way data binding an explicit declaration of expected keys/objects.

function defaultHealthDataState() {
  return {
    modified: false,
    initial: {
      answers: defaultData(),
    },
    latest: {
      answers: defaultData(),
    },
    shortAssessment: false,
    all: [],
    currentIndex: 0,
    error: null,
    nodata: true
  }
}

export default {
  namespaced: true,
  state: defaultHealthDataState(),
  getters: {
    // Wrapper for dynamically making updates to fields
    GET_DATA_FIELD(state) {
      return getField(state.latest.answers)
    },
    current(state) {
      return state.all[state.currentIndex];
    }
  },
  mutations: {
    UPDATE_SHORT_ASSESSMENT(state, isShortAssessment) {
      state.modified = true;

      state.shortAssessment = isShortAssessment;
    },
    UPDATE_DATA_FIELD(state, field) {
      state.modified = true;
      updateField(state.latest.answers, field);
    },
    SET_INITIAL_DATA(state, data) {
      state.all = data;
      state.latest = data[0];
      state.initial = data[0];
      state.nodata = false;
    },
    RESET(state) {
      state.latest = state.initial;
      state.modified = false;
    },
    CLEAR_USER_HEALTH_DATA(state){
      Object.assign(state, defaultHealthDataState());
    },
    UPDATE_INITIAL(state, data) {
      state.latest = data;
      state.initial = data;
      state.all.unshift(data);
      state.modified = false;
      state.nodata = false;
    },
    ERROR(state, err) {
      state.error = err;
    },
    CLEAR_ERROR(state) {
      state.error = null;
    },
    SET_CURRENT_INDEX(state, index) {
      state.currentIndex = index;
    }
  },
  actions: {
    GET_INITIAL_DATA({commit, dispatch, state}) {
      return new Promise((resolve,reject)=> {
        HealthData.all().then(res => {
          if (res.data.length > 0) commit('SET_INITIAL_DATA', res.data);
          commit('CLEAR_ERROR');
          resolve(res);
        }).catch(err => {
          if (err.response && err.response.status === 401) {
            commit('CLEAR_ERROR');
            resolve({});
          }
          commit('ERROR', err);
          reject(err);
        });
      })
    },
    SAVE_NEW_DATA({commit, dispatch, state}) {
      return new Promise((resolve, reject) => {
        HealthData.create({
          ...state.latest.answers,
          short_assessment: state.shortAssessment,
        }).then(res => {
          commit('UPDATE_INITIAL', res.data);
          commit('CLEAR_ERROR');
          resolve(res);
        }).catch(err => {
          commit('ERROR', err);
          reject(err);
        });
      });
    },
    RESET({commit, dispatch}) {
      commit('RESET');
    },
    SET_CURRENT({commit}, index) {
      commit('SET_CURRENT_INDEX', index);
    },
    CLEAR_USER_HEALTH_DATA({ commit, dispatch }) {
      commit('CLEAR_USER_HEALTH_DATA');
    }
  }
}
